import { reaction } from 'mobx'
import { type EditorView } from 'prosemirror-view'
import { Unsubscribe } from 'redux'
import store from 'redux/store'
import { COMMENTS_PLUGIN_KEY, getCommentsPluginState } from './Constants'
import { ReactViewProps, State } from './Types'
import { CollabItemInlineTooltip } from 'components/CollabItemInlineTooltip/CollabItemInlineTooltip'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Provider } from 'react-redux'
import { Comment } from 'types'
import { findAllCommentMarks, firstVisibleParent } from './utils'
import { inline, offset, shift, flip, hide, autoUpdate, computePosition } from '@floating-ui/dom'
import { observer } from 'mobx-react-lite'
import { Selection } from 'prosemirror-state'
import { reportError } from 'utils/error'
import { Mark } from 'prosemirror-model'
import { schema } from 'prose-core'
import scrollIntoView from 'scroll-into-view-if-needed'
import { DocStore } from 'stores/DocStore'
import { createRoot, Root } from 'react-dom/client'

const ReactView = observer(({ queryClient, docStore }: ReactViewProps) => {
  const userId = docStore.rootStore.comments.highlighted?.user.id

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        {userId && <CollabItemInlineTooltip userId={userId} />}
      </QueryClientProvider>
    </Provider>
  )
})

function selectedCommentUuids(editorView: EditorView): Set<string> {
  let { $from, empty, ranges } = editorView.state.selection
  const uuids = new Set<string>()

  const processAttrs = (n: { attrs?: { uuid?: string; uuids?: string[] } }) => {
    if (n.attrs?.uuid) uuids.add(n.attrs.uuid)
    if (n.attrs?.uuids) n.attrs.uuids.map((uuid: string) => uuids.add(uuid))
  }
  const processMark = (m: Mark<any>) => {
    if (m.type !== schema.marks.comment) return
    processAttrs(m)
  }

  if (empty) {
    if (!editorView.state.storedMarks) {
      $from.marks().forEach(processMark)
    } else {
      editorView.state.storedMarks.forEach(processMark)
    }
  } else {
    ranges.forEach((r) => {
      editorView.state.doc.nodesBetween(r.$from.pos, r.$to.pos, (n) => {
        n.marks.forEach(processMark)
      })
    })
  }

  const nodeAtFrom = editorView.state.doc.nodeAt($from.pos)
  if (nodeAtFrom) processAttrs(nodeAtFrom)

  return uuids
}

export class CommentsView {
  view: EditorView
  reduxUnsubscribe: Unsubscribe
  disposers: Array<() => void> = []
  queryClient: QueryClient

  /**
   * Tooltip container
   */
  widget: HTMLElement
  /**
   * Tooltip anchor
   */
  anchor: Element | null = null
  /**
   * Whether the floating ui has been initialised
   */
  initialized: boolean = false
  /**
   * Callback to clean up floating ui listeners
   */
  cleanupFloating?: () => void
  root: Root

  constructor(view: EditorView) {
    this.view = view
    this.reduxUnsubscribe = store.subscribe(() => {
      this.view.dispatch(this.view.state.tr)
    })

    const { queryClient }: State = getCommentsPluginState(view.state)
    this.queryClient = queryClient

    this.setupReactions()

    this.widget = document.createElement('div')
    this.widget.style.position = 'absolute'
    this.widget.style.display = 'none'
    this.root = createRoot(this.widget)
    this.addWidgetToDom()
  }

  get enabled() {
    return getCommentsPluginState(this.view.state).enabled
  }

  get docStore(): DocStore | undefined {
    return getCommentsPluginState(this.view.state).docStore
  }

  setupReactions = () => {
    this.disposers = [
      reaction(() => this.docStore?.docMode, this.handleDocModeChange),
      reaction(() => this.docStore?.selectedUuid, this.handleSelect),
      reaction(() => this.docStore?.highlightedUuid, this.handleHighlight),
      reaction(
        () => this.docStore?.rootStore.comments,
        () => this.view.dispatch(this.view.state.tr),
      ),
    ]
  }

  handleDocModeChange = () => {
    const tr = this.view.state.tr
    tr.setMeta(COMMENTS_PLUGIN_KEY, { enabled: true })
    this.view.dispatch(tr)
  }

  handleSelect = () => {
    if (!this.enabled) return

    const comment = this.docStore?.rootStore.comments.selected
    if (comment) {
      const markUuids = selectedCommentUuids(this.view)
      const anchor = findAllCommentMarks(comment.uuid)[0]
      // The cursor is currently placed on the node we are trying to select
      if (comment.uuid != null && markUuids.has(comment.uuid)) {
        scrollIntoView(anchor, { block: 'center', inline: 'nearest', behavior: 'smooth' })
        return
      }
      const pos = this.view.posAtDOM(anchor, 0)
      // The item we are trying to select does not exist
      if (pos < 0) return
      const $pos = this.view.state.doc.resolve(pos)
      // This fixes the selection of smart links / cards / frames
      const sel = Selection.findFrom($pos, 1)
      if (sel == null) {
        reportError('Unexpected null selection while handling a comment selection.', { severity: 'error' })
        return
      }
      this.view.focus()
      this.view.dispatch(this.view.state.tr.setSelection(sel))
      scrollIntoView(anchor, { block: 'center', inline: 'nearest', behavior: 'smooth' })
    }
    this.view.dispatch(this.view.state.tr)
  }

  handleHighlight = () => {
    if (!this.enabled) return

    if (this.docStore?.rootStore.comments.highlighted) {
      this.showTooltip(this.docStore?.rootStore.comments.highlighted)
    } else {
      this.hideTooltip()
    }
    this.view.dispatch(this.view.state.tr)
  }

  addWidgetToDom() {
    if (!this.docStore) {
      return
    }
    if (this.view.dom.parentNode) {
      this.view.dom.parentNode.appendChild(this.widget)
      this.root.render(<ReactView queryClient={this.queryClient} docStore={this.docStore} />)
      this.initialized = true
    }
  }

  hideTooltip() {
    this.widget.style.display = 'none'
    this.cleanupFloating?.()
    this.cleanupFloating = undefined
  }

  showTooltip(comment: Comment) {
    this.cleanupFloating?.()
    this.cleanupFloating = undefined

    const anchor = firstVisibleParent(findAllCommentMarks(comment.uuid)[0])
    if (this.anchor !== anchor) {
      this.anchor = anchor
    }
    if (!this.anchor) {
      this.hideTooltip()
      return
    }

    this.widget.style.display = 'block'

    if (this.cleanupFloating || !this.anchor) {
      return
    }

    this.cleanupFloating = autoUpdate(
      this.anchor,
      this.widget,
      () => {
        if (!this.anchor) {
          return
        }
        computePosition(this.anchor, this.widget, {
          placement: 'top',
          middleware: [inline(), offset(2), flip(), shift(), hide({ strategy: 'escaped', padding: 40 })],
        }).then(({ x, y, middlewareData }) => {
          Object.assign(this.widget.style, {
            top: '0',
            left: '0',
            transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            display: middlewareData.hide!.escaped ? 'none' : 'block',
            zIndex: 1,
          })
        })
      },
      {
        animationFrame: true,
      },
    )
  }

  update(view: EditorView) {
    this.view = view
    if (!this.initialized) {
      this.addWidgetToDom()
    }
  }

  destroy() {
    this.reduxUnsubscribe()
    this.disposers.forEach((dispose) => dispose())
    setTimeout(() => this.root.unmount(), 0)
    this.cleanupFloating?.()
    this.widget.remove()
  }
}
